/**
 * Selbstheilung für veraltete Builds und abgebrochene Chunk-Ladungen
 * (Stale-Build-Reload).
 *
 * Hintergrund (Sentry CHECKPOINT-EDU-7, 11.09.2026): Next 16 salzt die IDs der
 * Server Actions pro Build. Trifft ein Tab mit altem Client-Bundle auf den
 * neuen Server (Skew-Fenster abgelaufen oder Skew-Schwelle gesetzt), antwortet
 * der Server mit «Action not found», der Client wirft `UnrecognizedActionError`.
 * Zweiter Fall (CHECKPOINT-EDU-1P 11.09., -1R 14.09.): Die Turbopack-/Webpack-
 * Laufzeit wirft `ChunkLoadError` («Failed to load chunk … from module …»),
 * wenn ein JS-Chunk nicht ladbar ist, sei es, weil er zum alten Build gehört
 * und nicht mehr ausgeliefert wird, oder weil das Netz beim Nachladen abbricht.
 * Heilen kann beides nur ein harter Reload, danach läuft das aktuelle Bundle
 * mit frischen Chunks. Boundaries (useBoundaryError) und StaleBuildGuard lösen
 * diesen Reload einmal automatisch aus, statt Fehlerseite oder wirkungslosen
 * Klick zu zeigen.
 *
 * Schleifenschutz, bewusst mehrfach und fail-closed:
 * 1. Nur diese zwei Fehlernamen. Jeder andere Fehler zeigt die Boundary wie
 *    bisher.
 * 2. Marker in sessionStorage (pro Tab, überlebt den Reload, stirbt mit dem
 *    Tab) wird VOR dem Reload geschrieben und zurückgelesen. Nur ein wirklich
 *    persistierter Marker erlaubt den Reload. Wirft der neue Build denselben
 *    Fehler sofort wieder, greift beim nächsten Mount der Cooldown.
 * 3. Cooldown 10 Minuten zwischen zwei automatischen Reloads.
 * 4. Höchstens 2 automatische Reloads pro Tab-Sitzung, danach immer die
 *    Boundary mit manuellem Knopf.
 * 5. Ohne sessionStorage (Privatmodus, gesperrte Site-Daten, Exception) kein
 *    automatischer Reload: ohne Marker ist Schleifenfreiheit nicht garantierbar.
 * 6. Uhr springt zurück oder `now` ist keine Zahl: kein Reload.
 *
 * Modul ist DOM-frei und rein, damit der Schutz in Node getestet werden kann
 * (src/lib/staleBuildReload.test.ts, inkl. Schleifen-Simulation).
 */

export const STALE_BUILD_ERROR_NAME = 'UnrecognizedActionError';
export const CHUNK_LOAD_ERROR_NAME = 'ChunkLoadError';
/** Fehlernamen, die ein harter Reload heilt. Namen sind stabil, Texte werden gescrubbt. */
export const STALE_BUILD_ERROR_NAMES: ReadonlySet<string> = new Set([
  STALE_BUILD_ERROR_NAME,
  CHUNK_LOAD_ERROR_NAME,
]);
export const STALE_BUILD_RELOAD_KEY = 'checkpoint:stale-build-reload';
export const STALE_BUILD_RELOAD_COOLDOWN_MS = 10 * 60_000;
export const STALE_BUILD_RELOAD_MAX_PER_TAB = 2;

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

interface ReloadMarker {
  count: number;
  last: number;
}

/** «Server Action nicht gefunden» (Next) oder «Chunk nicht ladbar» (Turbopack/Webpack). */
export function isStaleBuildError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const name = (error as { name?: unknown }).name;
  return typeof name === 'string' && STALE_BUILD_ERROR_NAMES.has(name);
}

/** sessionStorage, falls erreichbar; jeder Zugriffsfehler ergibt null (→ kein Auto-Reload). */
export function getSessionStorage(): StorageLike | null {
  try {
    const storage = globalThis.sessionStorage;
    return storage ?? null;
  } catch {
    return null;
  }
}

function readMarker(storage: StorageLike): ReloadMarker | null {
  const raw = storage.getItem(STALE_BUILD_RELOAD_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      const { count, last } = parsed as Record<string, unknown>;
      if (
        typeof count === 'number' && Number.isFinite(count) &&
        typeof last === 'number' && Number.isFinite(last)
      ) {
        return { count: Math.max(0, count), last };
      }
    }
  } catch {
    // Korrupter Marker: wie kein Marker. Der nächste Anspruch schreibt einen gültigen.
  }
  return null;
}

/**
 * Beansprucht einen automatischen Reload. Gibt nur dann true zurück, wenn alle
 * Schutzregeln erfüllt sind UND der Marker nachweislich gespeichert wurde.
 * Der Aufrufer darf `window.location.reload()` ausschliesslich bei true auslösen.
 */
export function claimStaleBuildReload(storage: StorageLike | null | undefined, now: number): boolean {
  if (!storage || typeof now !== 'number' || !Number.isFinite(now)) return false;
  try {
    const marker = readMarker(storage);
    if (marker) {
      if (marker.count >= STALE_BUILD_RELOAD_MAX_PER_TAB) return false;
      // Negative Differenz (Uhr zurückgesprungen) blockt ebenfalls.
      if (now - marker.last < STALE_BUILD_RELOAD_COOLDOWN_MS) return false;
    }
    const next: ReloadMarker = { count: (marker?.count ?? 0) + 1, last: now };
    const serialized = JSON.stringify(next);
    storage.setItem(STALE_BUILD_RELOAD_KEY, serialized);
    return storage.getItem(STALE_BUILD_RELOAD_KEY) === serialized;
  } catch {
    return false;
  }
}
